import React from 'react'

import { useNavigation } from '@zet/common/hooks/useNavigation'
import { colors as zetColors } from '@zet/design-system'
import { Button } from '@zet/design-system/button'
import { Icon } from '@zet/design-system/icons'
import { Stack, XStack, YStack } from '@zet/design-system/layout'
import { B3, B4, B5, BTN, SH3 } from '@zet/design-system/typography'
import useBrandStore from '@zet/feature-brands/store/brands'
import { useUserContext } from '@zet/feature-user'
import { i18n } from '@zet/localization'
import { LeadStatus } from '@zet/utils/Constants'
import { LoggedInNavigation } from '@zet/utils/interface'
import moment from 'moment'

type LeadAddedProps = {
  item?: object
  videoId?: string
  brandName?: string
  inactive?: boolean
}

const LeadAdded = ({ item, videoId, brandName, inactive }: LeadAddedProps) => {
  const { navigate } = useNavigation<LoggedInNavigation>()
  // const [isBottomSheetVisible, setIsBottomSheetVisible] =
  //   useState<boolean>(false)
  const status = item?.status
  const video = videoId
  const partnerId = item?.partnerId
  const customerId = item?.customerId
  const customerPhoneNumber = item?.phoneNumber
  const { data: user } = useUserContext()
  const { setLeadSource } = useBrandStore()

  if (status == LeadStatus.LEAD_ADDED) {
    return (
      <Stack className="border-secondary-100 bg-neutral-white100 rounded-sm border-l-4">
        <XStack className="py-4/2 px-3">
          {item?.isReopened ? (
            <Stack className="border-secondary-100 absolute left-[30] h-4 w-1" />
          ) : null}
          <Stack className="bg-secondary-100 absolute left-3 top-3 items-center rounded-lg">
            <Icon
              type={'LinkOutlined'}
              size={20}
              color={zetColors.neutral['white100']}
              className="m-1.5"
            />
          </Stack>
          {item?.createDate ? (
            <YStack className="left-6">
              <SH3 className=" text-grays-100">
                {item?.isReopened
                  ? i18n.t('leads.linkReShared')
                  : i18n.t('leads.linkShared')}
              </SH3>
              <B4 className=" text-grays-50">
                {moment.utc(item?.createDate).format('DD-MMM-YYYY')}
              </B4>
            </YStack>
          ) : (
            <YStack className="left-6 mt-2">
              <SH3 className=" text-grays-100">
                {item?.isReopened
                  ? i18n.t('leads.linkReShared')
                  : i18n.t('leads.linkShared')}
              </SH3>
            </YStack>
          )}
        </XStack>

        {item?.hasNoUpdate ? (
          <Stack className="bg-warning-8 mx-3 mt-2 rounded">
            <B3 className="px-4/2 text-grays-60  py-2">
              {i18n.t('leads.linkSharedDescription')}
            </B3>
          </Stack>
        ) : (
          <Stack className="bg-secondary-10 mx-3 mt-2 rounded">
            <B3 className="px-4/2 text-grays-60  py-2">
              {i18n.t('leads.linkSharedDescription')}
            </B3>
          </Stack>
        )}

        {video ? (
          <Stack className="mx-3 mt-2">
            <Button
              fullWidth
              intent
              size={'sm'}
              className={'px-4/2 items-center justify-center py-2'}
              onPress={() =>
                navigate('ApplicationProcess', { brandId: partnerId })
              }
              eventName="click_action"
              eventPayload={{
                item_name: 'lead_page_learn_about_application_process',
                lead_id: item?.id,
                lead_status: status,
                user_id: user?.id,
                customer_id: customerId,
                partner_id: partnerId,
                brand_name: brandName,
              }}
            >
              <XStack className="items-center">
                <Icon
                  type="VideoOutlined"
                  size={20}
                  color={zetColors.primary[50]}
                  className={'mr-2'}
                />
                <BTN className="text-primary-100 text-center">
                  {i18n.t('leads.videoButton')}
                </BTN>
              </XStack>
            </Button>
          </Stack>
        ) : null}

        {item?.hasNoUpdate ? (
          <Stack className="bg-grays-20 mx-3 mt-2 rounded p-1">
            <XStack className="px-1 py-1">
              <Icon
                type={'ErrorOutlined'}
                size={20}
                color={zetColors.warning[150]}
              />
              <B5 className="text-grays-60 flex-1 px-2">
                {i18n.t('leads.hasNoUpdate')}
              </B5>
            </XStack>
          </Stack>
        ) : null}

        {item?.hasNoUpdate ? (
          <Stack className="mb-4/2 mx-3 mt-2">
            <Button
              disabled={inactive}
              fullWidth
              size={'md'}
              className={'px-3 py-2'}
              onPress={() => {
                setLeadSource(item?.source)
                navigate('ShareCardLink', {
                  brandId: partnerId,
                  customerName: item?.name,
                  customerPhoneNumber: customerPhoneNumber,
                  customerId: customerId,
                  reopen: false,
                  shareButtonMessage: {
                    en: `Hi ${item?.name},\n\nYour application is incomplete for ${brandName}. Complete now using this link:\n\n{{URL}}  \n\n{{agentName}}\nBusiness Consultant`,
                    hi: `हेलो ${item?.name},\n\nआपका ${brandName} का आवेदन पेंडिंग है। नीचे दी गई लिंक के ज़रिए आवेदन पूरा करें:\n\n{{URL}}\n\n{{agentName}}\nबिज़नेस कंसलटेंट`,
                  },
                  routeName: 'forCustomerFlow',
                  eventName: 'click_action',
                  eventPayload: {
                    item_name: 'lead_page_share_message_preview_screen_share',
                    lead_id: item?.id,
                    lead_status: status,
                    user_id: user?.id,
                    customer_id: customerId,
                    partner_id: partnerId,
                    brand_name: brandName,
                  },
                })
              }}
              eventName="click_action"
              eventPayload={{
                item_name: 'lead_page_send_reminder',
                lead_id: item?.id,
                lead_status: status,
                user_id: user?.id,
                customer_id: customerId,
                partner_id: partnerId,
                brand_name: brandName,
              }}
            >
              {inactive ? (
                <XStack className="items-center">
                  <BTN className=" text-neutral-white100">
                    {i18n.t('leads.inactiveShare')}
                  </BTN>
                </XStack>
              ) : (
                <XStack className="items-center">
                  <Icon
                    type="MessageOutlined"
                    size={24}
                    color={zetColors.neutral['white100']}
                    className={'mr-2'}
                  />
                  <BTN className=" text-neutral-white100">
                    {i18n.t('leads.sendRemainder')}
                  </BTN>
                </XStack>
              )}
            </Button>
          </Stack>
        ) : (
          <Stack className="mb-4/2 mx-3 mt-2">
            <Button
              disabled={inactive ? true : false}
              fullWidth
              size={'md'}
              className={'px-3 py-2'}
              onPress={() => {
                setLeadSource(item?.source)
                navigate('ShareCardLink', {
                  brandId: partnerId,
                  customerName: item?.name,
                  customerPhoneNumber: customerPhoneNumber,
                  customerId: customerId,
                  reopen: false,
                  routeName: 'forCustomerFlow',
                  eventName: 'click_action',
                  eventPayload: {
                    item_name: 'lead_page_share_message_preview_screen_share',
                    lead_id: item?.id,
                    lead_status: status,
                    user_id: user?.id,
                    customer_id: customerId,
                    partner_id: partnerId,
                    brand_name: brandName,
                  },
                })
              }}
              eventName="click_action"
              eventPayload={{
                item_name: 'lead_page_share_link_again',
                lead_id: item?.id,
                lead_status: status,
                user_id: user?.id,
                customer_id: customerId,
                partner_id: partnerId,
                brand_name: brandName,
              }}
            >
              {inactive ? (
                <XStack className="items-center">
                  <BTN className=" text-neutral-white100">
                    {i18n.t('leads.inactiveShare')}
                  </BTN>
                </XStack>
              ) : (
                <XStack className="items-center">
                  <Icon
                    type="Share"
                    size={24}
                    color={zetColors.neutral['white100']}
                    className={'mr-2'}
                  />
                  <BTN className=" text-neutral-white100">
                    {i18n.t('leads.resahre')}
                  </BTN>
                </XStack>
              )}
            </Button>
          </Stack>
        )}
      </Stack>
    )
  } else if (status == LeadStatus.EXPIRED) {
    return (
      <XStack className="border-grays-30 bg-neutral-white100 py-4/2 rounded-sm border-l-4 px-3">
        <Stack className="bg-grays-30 absolute left-[30] h-4 w-1" />
        <Stack className=" bg-grays-30 absolute left-3 top-3 items-center rounded-lg">
          <Icon
            type={'DoneOutlined'}
            size={20}
            color={zetColors.grays[50]}
            className="m-1.5"
          />
        </Stack>
        {item?.createDate ? (
          <Stack className="left-6 justify-center">
            <SH3 className=" text-grays-60">{i18n.t('leads.linkShared')}</SH3>
            <B4 className="text-grays-50">
              {moment.utc(item?.createDate).format('DD-MMM-YYYY')}
            </B4>
          </Stack>
        ) : (
          <Stack className="left-6 mt-2 justify-center">
            <SH3 className=" text-grays-60">{i18n.t('leads.linkShared')}</SH3>
          </Stack>
        )}
      </XStack>
    )
  } else {
    return (
      <XStack className="border-grays-30 bg-neutral-white100 py-4/2 rounded-sm border-l-4 px-3 ">
        <Stack className="bg-success-8 absolute left-3 top-3 items-center rounded-lg">
          <Icon
            type={'LinkOutlined'}
            size={20}
            color={zetColors.success[100]}
            className="m-1.5"
          />
        </Stack>
        {item?.createDate ? (
          <YStack className="left-6  justify-center">
            <SH3 className=" text-grays-100">{i18n.t('leads.linkShared')}</SH3>
            <B4 className="text-grays-50">
              {moment.utc(item?.createDate).format('DD-MMM-YYYY')}
            </B4>
          </YStack>
        ) : (
          <YStack className="left-6  mt-2 justify-center">
            <SH3 className=" text-grays-100">{i18n.t('leads.linkShared')}</SH3>
          </YStack>
        )}
      </XStack>
    )
  }
}

export default LeadAdded
